<script setup lang="ts">
import { ref, watch, h, type Component, nextTick } from 'vue';

import { config } from '@/config';

import { FileExtension } from '@/helpers/types.ts';
import { QSpinnerGears } from 'quasar';
import { checkFileExtension } from '@/helpers/checkFileExtension.ts';

const props = defineProps<{
  fileId?: string;
  fileName?: string;
}>();

const localId = ref('');
const localFileName = ref('');
const localExtension = ref<FileExtension | undefined>(undefined);
const component = ref<Component>();
const imageExtensions = ['png', 'jpeg', 'jpg', 'webp'];
const isImage = ref(false);
const loading = ref(false);

const isImageExtension = (extension: string | undefined): boolean => {
  return extension ? imageExtensions.includes(extension.toLowerCase()) : false;
};

const computeExtension = async () => {
  try {
    if (!localId.value) return;
    const ext = await checkFileExtension(localFileName.value, localId.value);
    localExtension.value = ext ?? undefined;
  } catch (e) {
    console.warn('FileViewer: failed to detect extension', e);
    localExtension.value = undefined;
  }
};

const renderFile = async () => {
  // Проверяем валидность localId
  if (!localId.value || localId.value.trim() === '') {
    console.warn('FileViewer: localId is empty or invalid:', localId.value);
    loading.value = false;
    return;
  }

  loading.value = true;

  try {
    await nextTick();

    const currentIsImage = isImageExtension(localExtension.value);
    if (currentIsImage && !isImage.value) {
      isImage.value = true;
    } else if (!currentIsImage && isImage.value) {
      isImage.value = false;
    }

    if (!currentIsImage) {
      switch (localExtension.value?.toLowerCase()) {
        case 'doc':
        case 'xlsx':
        case 'docx':
          component.value = h('iframe', {
            src: `https://view.officeapps.live.com/op/embed.aspx?src=${config.filestorage}/${localId.value}`,
            class: 'viewer__frame-view',
            height: '100%',
            width: '100%',
          });
          break;
        case 'pdf': {
          const pdfSrc = `${config.filestorage}/show/${localId.value}#view=FitH&embedded=true&scrollbar=0&toolbar=0&statusbar=0&messages=0&navpanes=0`;

          component.value = h('iframe', {
            // src: `https://docs.google.com/gview?url=${config.filestorage}/${localId.value}&toolbar=0&embedded=true`,
            src: pdfSrc,
            class: 'viewer__frame-view',
            height: '100%',
            width: '100%',
          });
          break;
        }
        default:
          component.value = h('iframe', {
            src: `https://view.officeapps.live.com/op/embed.aspx?src=${config.filestorage}/${localId.value}`,
            class: 'viewer__frame-view',
            height: '100%',
            width: '100%',
          });
          break;
      }
    }
  } catch (error) {
    console.error('FileViewer: Error loading file:', error);
    component.value = h('div', 'Error loading file');
  } finally {
    // Небольшая задержка для показа состояния загрузки
    setTimeout(() => {
      loading.value = false;
    }, 100);
  }
};

// Дебаунс для предотвращения множественных вызовов
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
const debouncedUpdate = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(async () => {
    await computeExtension();
    await renderFile();
  }, 50);
};

watch(() => props.fileId, (newId, oldId) => {
  if (newId !== oldId) {
    localId.value = newId ?? '';
    debouncedUpdate();
  }
}, { immediate: true });

watch(() => props.fileName, (newName, oldName) => {
  if (newName !== oldName) {
    localFileName.value = newName ?? '';
    debouncedUpdate();
  }
}, { immediate: true });
</script>

<template>
  <div class="viewer row items-center justify-center">
    <QSpinnerGears
      v-if="loading"
      color="primary"
      size="20em"
    />
    <img
      v-if="!loading && isImage"
      :src="`${config.filestorage}/${localId}`"
      class="image-preview"
      alt="image"
    >
    <component
      :is="component"
      v-else
    />
  </div>
</template>

<style lang="scss" scoped>
.viewer {
  height: 100%;
  width: 100%;
  overflow: hidden;

  &__frame-view {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__image-view {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
.image-preview {
  max-width: 100%;
  max-height: 100%;
}
</style>
